"use client";
import * as React from "react";
import { useState, useEffect } from "react";

const FeatureBlock = ({
  title,
  description,
  position,
  className = "",
  onHover,
  onLeave,
  imageIndex,
  isHovered,
}) => {
  const positionClasses = {
    "top-left": "lg:absolute lg:top-8 lg:left-12 lg:w-64",
    "top-right": "lg:absolute lg:top-8 lg:right-12 lg:w-64",
    "bottom-left": "lg:absolute lg:bottom-8 lg:left-12 lg:w-64",
    "bottom-right": "lg:absolute lg:bottom-8 lg:right-12 lg:w-64",
  };

  return (
    <div
      className={`${positionClasses[position]} ${className} group cursor-pointer z-20 relative`}
      onMouseEnter={() => onHover(imageIndex)}
      onMouseLeave={onLeave}
      role="button"
      tabIndex={0}
      aria-label={`Feature: ${title}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className={`w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg transition-transform duration-300 ${
            isHovered ? "scale-125" : "group-hover:scale-110"
          }`}
        >
          <div className="w-3 h-3 bg-white rounded-full"></div>
        </div>
        <div className="flex-1">
          <h3
            className={`text-base lg:text-lg md:text-base sm:text-sm font-bold mb-2 leading-tight transition-colors duration-300 ${
              isHovered ? "text-indigo-600" : "text-slate-900"
            }`}
          >
            {title}
          </h3>
          <p
            className={`text-xs lg:text-sm md:text-xs sm:text-xs leading-relaxed transition-colors duration-300 ${
              isHovered ? "text-slate-700" : "text-slate-600"
            }`}
          >
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

const CircularImage = ({
  imageUrl,
  alt,
  className = "",
  isHovered = false,
}) => {
  return (
    <div
      className={`absolute w-24 h-24 lg:w-32 lg:h-32 md:w-24 md:h-24 sm:w-20 sm:h-20 shadow-xl border-4 border-white rounded-full overflow-hidden transition-all duration-300 z-10 ${
        isHovered ? "scale-125 shadow-2xl border-indigo-300" : "scale-100"
      } ${className}`}
      role="img"
      aria-label={alt}
    >
      <img src={imageUrl} alt={alt} className="w-full h-full object-cover" />
    </div>
  );
};

const MobileFeatureItem = ({
  title,
  description,
  imageUrl,
  alt,
  isReversed,
  showConnector,
  index,
}) => {
  return (
    <div className="relative">
      <div
        className={`flex items-center gap-4 ${isReversed ? "flex-row-reverse" : "flex-row"} mb-8`}
      >
        {/* Image */}
        <div className="w-16 h-16 rounded-full overflow-hidden shadow-lg border-4 border-white flex-shrink-0">
          <img
            src={imageUrl}
            alt={alt}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Feature Content */}
        <div className="flex-1">
          <div className="flex items-start gap-3">
            <div className="hidden md:block w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0 mt-1">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
            <div className="flex-1">
              <h3 className="text-md font-bold text-blue-800 md:text-slate-900 mb-1 leading-tight">
                {title}
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                {description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Dotted Connector Line */}
      {showConnector && (
        <div className="absolute left-8 top-20 w-0.5 h-8 flex justify-center">
          <svg width="2" height="32" className="overflow-visible">
            <defs>
              <pattern
                id={`mobile-dots-${index}`}
                patternUnits="userSpaceOnUse"
                width="4"
                height="4"
              >
                <circle cx="2" cy="2" r="1" fill="#6366f1" opacity="0.6" />
              </pattern>
            </defs>
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="32"
              stroke={`url(#mobile-dots-${index})`}
              strokeWidth="2"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

export default function WhyChooseUs() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredImage, setHoveredImage] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleFeatureHover = (imageIndex) => {
    setHoveredImage(imageIndex);
  };

  const handleFeatureLeave = () => {
    setHoveredImage(null);
  };

  // Image URLs that correspond to each feature
  const imageUrls = [
    "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=400&fit=crop&crop=center", // AI/Automation
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=400&fit=crop&crop=center", // Analytics/Performance
    "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=400&fit=crop&crop=center", // Cloud Infrastructure
    "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=400&fit=crop&crop=center", // API Integration
  ];

  const features = [
    {
      title: "AI-Powered Automation",
      description:
        "Automate repetitive workflows, improve operational efficiency, and boost productivity using intelligent AI-driven systems.",
      alt: "AI Technology and Automation",
    },
    {
      title: "Business Performance Insights",
      description:
        "Track business growth, customer engagement, analytics, and operational performance with real-time dashboards.",
      alt: "Business Analytics and Performance",
    },
    {
      title: "Cloud & Scalable Infrastructure",
      description:
        "Deploy secure, scalable, and cloud-native applications with enterprise-grade performance and reliability.",
      alt: "Cloud Infrastructure and Scalability",
    },
    {
      title: "API Integrations & Smart Systems",
      description:
        "Connect CRM systems, payment gateways, automation tools, and third-party platforms seamlessly.",
      alt: "API Integration and Smart Systems",
    },
  ];

  return (
    <section
      className="relative py-8 lg:py-10 md:py-8 sm:py-6 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 overflow-hidden"
      aria-labelledby="why-choose-us-heading"
    >
      <style>{`
        @keyframes rotateDots {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .rotating-dots {
          animation: rotateDots 20s linear infinite;
        }
      `}</style>

      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 lg:w-80 lg:h-80 md:w-60 md:h-60 sm:w-40 sm:h-40 bg-blue-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 lg:w-80 lg:h-80 md:w-60 md:h-60 sm:w-40 sm:h-40 bg-indigo-200/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container max-w-6xl mx-auto px-4 lg:px-6 md:px-4 sm:px-3 relative">
        {/* Section Header */}
        <header className="text-center mb-6 lg:mb-8 md:mb-6 sm:mb-4">
          <div className="inline-flex items-center px-4 py-2 lg:px-6 lg:py-2 md:px-4 md:py-1.5 sm:px-3 sm:py-1 bg-[#1719A8]/90 backdrop-blur-md border border-[#1719A8]/60 rounded-full mb-3 shadow-lg">
            <span className="w-2 h-2 bg-white rounded-full mr-3 animate-pulse"></span>
            <span className="text-xs md:text-sm lg:text-sm sm:text-xs text-white font-semibold uppercase tracking-wider">
              Why Choose Vasta?
            </span>
          </div>
          <h1
            id="why-choose-us-heading"
            className="text-3xl lg:text-5xl md:text-3xl sm:text-2xl font-bold text-slate-900 mb-4 lg:mb-5 md:mb-4 sm:mb-3 leading-tight"
          >
            Accelerate Your Business With
            <br />
            <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
              Smart Digital Solutions
            </span>
          </h1>
          <p className="text-lg lg:text-xl md:text-base sm:text-sm text-slate-600 max-w-3xl mx-auto lg:max-w-4xl md:max-w-2xl sm:max-w-xl leading-relaxed">
            We help startups and enterprises scale faster using AI-powered
            automation, cloud infrastructure, enterprise software development,
            and business growth strategies.
          </p>
        </header>

        {/* Desktop Layout */}
        <div className="relative max-w-5xl mx-auto hidden lg:block">
          {/* Feature Layout Container */}
          <div className="relative lg:h-[400px] md:h-[350px] sm:h-[300px] flex items-center justify-center">
            {/* Feature Blocks and Images */}
            <div
              className={`relative w-full h-full transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            >
              {/* Top Left Feature - AI-Powered Automation */}
              <FeatureBlock
                title="AI-Powered Automation"
                description="Automate repetitive workflows, improve operational efficiency, and boost productivity using intelligent AI-driven systems."
                position="top-left"
                onHover={handleFeatureHover}
                onLeave={handleFeatureLeave}
                imageIndex={0}
                isHovered={hoveredImage === 0}
              />

              {/* Top Left Image */}
              <CircularImage
                imageUrl={imageUrls[0]}
                alt="AI Technology and Automation"
                className="lg:top-4 lg:left-80 md:top-14 md:left-72 sm:top-12 sm:left-64"
                isHovered={hoveredImage === 0}
              />

              {/* Top Right Feature - Business Performance Insights */}
              <FeatureBlock
                title="Business Performance Insights"
                description="Track business growth, customer engagement, analytics, and operational performance with real-time dashboards."
                position="top-right"
                onHover={handleFeatureHover}
                onLeave={handleFeatureLeave}
                imageIndex={1}
                isHovered={hoveredImage === 1}
              />

              {/* Top Right Image */}
              <CircularImage
                imageUrl={imageUrls[1]}
                alt="Business Analytics and Performance"
                className="lg:top-4 lg:right-80 md:top-14 md:right-72 sm:top-12 sm:right-64"
                isHovered={hoveredImage === 1}
              />

              {/* Bottom Left Feature - Cloud & Scalable Infrastructure */}
              <FeatureBlock
                title="Cloud & Scalable Infrastructure"
                description="Deploy secure, scalable, and cloud-native applications with enterprise-grade performance and reliability."
                position="bottom-left"
                onHover={handleFeatureHover}
                onLeave={handleFeatureLeave}
                imageIndex={2}
                isHovered={hoveredImage === 2}
              />

              {/* Bottom Left Image */}
              <CircularImage
                imageUrl={imageUrls[2]}
                alt="Cloud Infrastructure and Scalability"
                className="lg:bottom-4 lg:left-80 md:bottom-14 md:left-72 sm:bottom-12 sm:left-64"
                isHovered={hoveredImage === 2}
              />

              {/* Bottom Right Feature - API Integrations & Smart Systems */}
              <FeatureBlock
                title="API Integrations & Smart Systems"
                description="Connect CRM systems, payment gateways, automation tools, and third-party platforms seamlessly."
                position="bottom-right"
                onHover={handleFeatureHover}
                onLeave={handleFeatureLeave}
                imageIndex={3}
                isHovered={hoveredImage === 3}
              />

              {/* Bottom Right Image */}
              <CircularImage
                imageUrl={imageUrls[3]}
                alt="API Integration and Smart Systems"
                className="lg:bottom-4 lg:right-80 md:bottom-14 md:right-72 sm:bottom-12 sm:right-64"
                isHovered={hoveredImage === 3}
              />

              {/* Central Connecting Dotted Circle */}
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{ zIndex: 5 }}
              >
                <div className="relative w-80 h-80 lg:w-96 lg:h-96 md:w-72 md:h-72 sm:w-60 sm:h-60">
                  <div className="absolute inset-0 w-full h-full border-2 border-dashed border-indigo-300/60 rounded-full rotating-dots"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Layout - Zig-zag pattern with dotted connectors */}
        <div className="lg:hidden mt-8">
          <div className="max-w-md mx-auto">
            {features.map((feature, index) => (
              <MobileFeatureItem
                key={index}
                title={feature.title}
                description={feature.description}
                imageUrl={imageUrls[index]}
                alt={feature.alt}
                isReversed={index % 2 === 1}
                showConnector={index < features.length - 1}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
